import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {{ items: HTMLElement[] }} Refs
 */

/**
 * Reveals the bundle offer matching the selected variant and adds the pair to the cart.
 *
 * @extends {Component<Refs>}
 */
export class NaBundleBannerComponent extends Component {
    #abortController = new AbortController();

    /** @type {number | null} */
    #resetTimeout = null;

    connectedCallback() {
        super.connectedCallback();

        const section = this.closest('.shopify-section');

        section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
            signal: this.#abortController.signal,
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.#abortController.abort();

        if (this.#resetTimeout !== null) {
            clearTimeout(this.#resetTimeout);
            this.#resetTimeout = null;
        }
    }

    /** @returns {HTMLElement[]} */
    get #items() {
        const { items } = this.refs;
        return Array.isArray(items) ? items : [];
    }

    /**
     * Each eligible variant has its own pre-priced item, so switching variants is a reveal.
     *
     * @param {Event} event
     */
    #handleVariantUpdate = (event) => {
        const variant = /** @type {any} */ (event).detail?.resource;
        if (!variant?.id) return;

        const selected = String(variant.id);
        let matched = false;

        for (const item of this.#items) {
            const isMatch = item.dataset.variantId === selected;
            item.hidden = !isMatch;
            if (isMatch) matched = true;
        }

        // A variant outside the eligible list has no offer to show.
        if (this.#items.length > 0) this.hidden = !matched;

        const imageSets = this.closest('.shopify-section')?.querySelectorAll('.na-spend-tiers__image-set') ?? [];

        for (const imageSet of imageSets) {
            if (imageSet instanceof HTMLElement) imageSet.hidden = imageSet.dataset.variantId !== selected;
        }
    };

    /**
     * @returns {Promise<{ item_count: number }>}
     */
    async #fetchCart() {
        const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });

        if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);

        return response.json();
    }

    /** @returns {string[]} */
    #getCartSectionIds() {
        return Array.from(document.querySelectorAll('cart-items-component'))
            .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
            .filter(/** @type {(id: string | undefined) => id is string} */ ((id) => Boolean(id)));
    }

    /**
     * Horizon's `on:click` listener is on `document`, so `currentTarget` is never the button.
     *
     * @param {Event} event
     * @returns {HTMLButtonElement | null}
     */
    #buttonFromEvent(event) {
        const target = event.target;

        if (target instanceof HTMLButtonElement) return target;
        if (target instanceof HTMLElement) return target.closest('button');

        return null;
    }

    /**
     * Adds both bundle products in one request. Called from `on:click`.
     *
     * @param {Event} event
     */
    async addBundle(event) {
        const button = this.#buttonFromEvent(event);

        if (!button || button.disabled) return;

        const { variantIds, defaultText, addingText, addedText, errorText } = button.dataset;
        const items = (variantIds || '')
            .split(',')
            .map((id) => Number(id.trim()))
            .filter((id) => Number.isFinite(id) && id > 0)
            .map((id) => ({ id, quantity: 1 }));

        if (items.length < 2) return;

        if (this.#resetTimeout !== null) clearTimeout(this.#resetTimeout);

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (addingText) button.textContent = addingText;

        try {
            const sectionIds = this.#getCartSectionIds();
            const response = await fetch(Theme.routes.cart_add_url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    items,
                    ...(sectionIds.length ? { sections: sectionIds, sections_url: window.location.pathname } : {}),
                }),
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.status) {
                throw new Error(
                    result.description || result.message || `Bundle add to cart failed: ${response.status}`
                );
            }

            const cart = await this.#fetchCart();

            document.dispatchEvent(
                new CartAddEvent(cart, this.id, {
                    source: 'na-bundle-banner',
                    itemCount: cart.item_count,
                    sections: result.sections,
                })
            );

            document.getElementById('na-cart-drawer')?.showDialog?.();

            if (addedText) button.textContent = addedText;
        } catch (error) {
            console.error(error);
            if (errorText) button.textContent = errorText;
        } finally {
            button.removeAttribute('aria-busy');
            this.#resetTimeout = setTimeout(() => {
                if (defaultText) button.textContent = defaultText;
                button.disabled = false;
                this.#resetTimeout = null;
            }, 2000);
        }
    }
}

if (!customElements.get('na-bundle-banner-component')) {
    customElements.define('na-bundle-banner-component', NaBundleBannerComponent);
}
